import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
export function Pricing() {
  return <>
      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-845" />

      <section className="section-padding bg-section-gradient relative overflow-hidden" data-id="element-846">
        {/* Subtle Ambient Glow Orbs */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-teal/5 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-847" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple/5 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-848" />

        <div className="inner-container relative z-10" data-id="element-849">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-850">
            <span className="text-purple text-sm font-poppins font-bold uppercase tracking-widest" data-id="element-851">
              Pricing
            </span>
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl leading-tight mt-4 text-white" data-id="element-852">
              One Price. <span className="text-teal" data-id="element-853">Everything.</span>
            </h2>
            <p className="text-steel text-lg mt-6" data-id="element-854">
              MISTR connects to what you already use — or we give you the full
              stack. No nickel-and-diming per feature.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-855">
            {/* MR Pro */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="relative bg-white/5 border border-white/10 rounded-3xl p-10 shadow-sm hover:shadow-xl hover:border-purple/30 transition-all group" data-id="element-856">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-purple to-transparent opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-857" />

              <div className="mb-8" data-id="element-858">
                <h3 className="font-poppins font-bold text-2xl text-white mb-2" data-id="element-859">
                  MR Pro
                </h3>
                <div className="flex items-baseline gap-2" data-id="element-860">
                  <span className="text-5xl font-poppins font-extrabold text-white" data-id="element-861">
                    $997
                  </span>
                  <span className="text-steel font-medium" data-id="element-862">/month</span>
                </div>
                <p className="text-steel mt-4 text-sm" data-id="element-863">
                  Everything you need to run your business on autopilot.
                </p>
              </div>

              <ul className="space-y-3 mb-8 text-sm text-steel" data-id="element-864">
                <li className="flex items-start gap-2" data-id="element-865">
                  <Check size={16} className="text-teal shrink-0 mt-0.5" data-id="element-866" />{' '}
                  Voice AI
                </li>
                <li className="flex items-start gap-2" data-id="element-867">
                  <Check size={16} className="text-teal shrink-0 mt-0.5" data-id="element-868" />{' '}
                  Slack + SMS
                </li>
                <li className="flex items-start gap-2" data-id="element-869">
                  <Check size={16} className="text-teal shrink-0 mt-0.5" data-id="element-870" /> Task
                  & PM
                </li>
                <li className="flex items-start gap-2" data-id="element-871">
                  <Check size={16} className="text-teal shrink-0 mt-0.5" data-id="element-872" />{' '}
                  Email monitoring
                </li>
                <li className="flex items-start gap-2" data-id="element-873">
                  <Check size={16} className="text-teal shrink-0 mt-0.5" data-id="element-874" /> PPC
                  Agent
                </li>
                <li className="flex items-start gap-2" data-id="element-875">
                  <Check size={16} className="text-teal shrink-0 mt-0.5" data-id="element-876" />{' '}
                  Creative AI
                </li>
                <li className="flex items-start gap-2" data-id="element-877">
                  <Check size={16} className="text-teal shrink-0 mt-0.5" data-id="element-878" />{' '}
                  Client numbers
                </li>
                <li className="flex items-start gap-2" data-id="element-879">
                  <Check size={16} className="text-teal shrink-0 mt-0.5" data-id="element-880" />{' '}
                  Weekly reporting
                </li>
              </ul>
              <button className="w-full py-3 rounded-lg bg-teal text-white font-poppins font-semibold hover:bg-teal/90 transition-colors shadow-[0_0_15px_rgba(7,133,183,0.3)]" data-id="element-881">
                Book a Strategy Call →
              </button>
            </motion.div>

            {/* MR Agency */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="relative bg-gradient-to-b from-navy-dark to-dark-bg border border-teal/30 rounded-3xl p-10 shadow-2xl overflow-hidden" data-id="element-882">
              {/* Glow effect */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-teal/20 rounded-full blur-[80px] pointer-events-none" data-id="element-883" />

              <div className="mb-8 relative z-10" data-id="element-884">
                <div className="inline-block px-3 py-1 bg-teal/20 border border-teal/30 text-teal text-xs font-bold uppercase tracking-wider rounded-full mb-4" data-id="element-885">
                  For Agencies
                </div>
                <h3 className="font-poppins font-bold text-2xl text-white mb-2" data-id="element-886">
                  MR Agency
                </h3>
                <div className="flex items-baseline gap-2" data-id="element-887">
                  <span className="text-5xl font-poppins font-extrabold text-white" data-id="element-888">
                    $2,997
                  </span>
                  <span className="text-steel font-medium" data-id="element-889">/month</span>
                </div>
                <p className="text-steel mt-4 text-sm" data-id="element-890">
                  White-label MR and sell it to your own clients.
                </p>
              </div>

              <ul className="space-y-3 mb-8 text-sm text-steel" data-id="element-891">
                <li className="flex items-start gap-2 font-semibold text-white" data-id="element-892">
                  <Check size={16} className="text-purple shrink-0 mt-0.5" data-id="element-893" />{' '}
                  Everything in Pro +
                </li>
                <li className="flex items-start gap-2" data-id="element-894">
                  <Check size={16} className="text-purple shrink-0 mt-0.5" data-id="element-895" />{' '}
                  White-label MR
                </li>
                <li className="flex items-start gap-2" data-id="element-896">
                  <Check size={16} className="text-purple shrink-0 mt-0.5" data-id="element-897" />{' '}
                  Revenue share model
                </li>
                <li className="flex items-start gap-2" data-id="element-898">
                  <Check size={16} className="text-purple shrink-0 mt-0.5" data-id="element-899" />{' '}
                  Dedicated onboarding
                </li>
                <li className="flex items-start gap-2" data-id="element-900">
                  <Check size={16} className="text-purple shrink-0 mt-0.5" data-id="element-901" />{' '}
                  Priority SLA
                </li>
              </ul>
              <button className="w-full py-3 rounded-lg border-2 border-purple text-purple font-poppins font-semibold hover:bg-purple hover:text-white transition-colors" data-id="element-902">
                Let's Talk →
              </button>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}